import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { API_URL, authFetch } from "../utils/auth";
import "../../css/client/InvoicePaymentPage.css";

const stripeCache = {};

function money(value, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "USD").toUpperCase() }).format(Number(value || 0));
}

function date(value) {
  return value ? new Date(value).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "Not set";
}

function stripePromiseFor(key) {
  if (!key) return null;
  if (!stripeCache[key]) stripeCache[key] = loadStripe(key);
  return stripeCache[key];
}

function methodLabel(method) {
  return method === "ach" ? "Bank" : "Card";
}

function stripeLoadErrorMessage(event) {
  const error = event?.error || event || {};
  const details = [error.type, error.code, error.decline_code, error.param].filter(Boolean).join(" / ");
  return [details, error.message || "We couldn't load the secure payment form. Please try again."].filter(Boolean).join(": ");
}

function EmbeddedPaymentForm({ invoice, intent, onProcessing, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const payLabel = `Pay ${money(intent.amount, invoice.currency)} by ${methodLabel(intent.payment_method)}`;

  async function submit(event) {
    event.preventDefault();
    if (!stripe || !elements || !paymentElementReady || loadError || !intent?.client_secret || submitting) return;

    setSubmitting(true);
    onError("");

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });

      if (result.error) {
        onError(result.error.message || "We couldn't process that payment. Please review your payment information and try again.");
        setSubmitting(false);
        return;
      }

      onProcessing(intent.payment_method === "ach" ? "Bank payment submitted" : "Payment submitted");
    } catch (err) {
      onError(err?.message || "We couldn't process that payment. Please review your payment information and try again.");
      setSubmitting(false);
    }
  }

  return (
    <form className="invoice-payment-element" onSubmit={submit}>
      <PaymentElement
        onReady={() => {
          setLoadError("");
          setPaymentElementReady(true);
        }}
        onLoadError={(event) => {
          setPaymentElementReady(false);
          const message = stripeLoadErrorMessage(event);
          setLoadError(message);
          onError(message);
        }}
      />
      {!paymentElementReady && !loadError && <p className="invoice-payment-loading">Loading secure payment form...</p>}
      {loadError && <p className="invoice-error-message">We couldn't load the secure payment form. Please try again.</p>}
      <button type="submit" disabled={!stripe || !elements || !paymentElementReady || loadError || submitting}>{submitting ? "Submitting..." : payLabel}</button>
    </form>
  );
}

export default function InvoicePaymentPage() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState("ach");
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [intentLoading, setIntentLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/invoices/${invoiceId}/`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Unable to load invoice.");
      setInvoice(data);
      setError("");
    } catch (err) {
      setError(err.message || "Unable to load invoice.");
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { loadInvoice(); }, [loadInvoice]);

  const options = useMemo(() => invoice?.payment_options || [], [invoice]);
  const achOption = options.find((item) => item.payment_method === "ach");
  const cardOption = options.find((item) => item.payment_method === "card");
  const selectedOption = selectedMethod === "ach" ? achOption : cardOption;
  const isPaid = invoice?.status === "paid";
  const isProcessing = invoice?.status === "processing";

  async function prepareIntent(method) {
    setSelectedMethod(method);
    setIntent(null);
    setMessage("");
    setError("");
    setIntentLoading(true);
    try {
      const response = await authFetch(`${API_URL}/invoices/${invoiceId}/payment-intent/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payment_method: method }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Unable to prepare payment.");
      setIntent(data);
    } catch (err) {
      setError(err.message || "Unable to prepare payment.");
    } finally {
      setIntentLoading(false);
    }
  }

  function processing(text) {
    setMessage(text);
    loadInvoice();
  }

  if (loading) return <main className="invoice-page"><section className="invoice-shell"><p>Loading invoice...</p></section></main>;
  if (error && !invoice) return <main className="invoice-page"><section className="invoice-shell"><h1>Invoice unavailable</h1><p>{error}</p></section></main>;

  return (
    <main className="invoice-page">
      <section className="invoice-shell">
        <header className="invoice-header">
          <div><span>South Central Development</span><h1>SCD Invoice</h1><p>{invoice.payment_type}</p></div>
          <strong className={`invoice-status invoice-status-${invoice.status}`}>{invoice.status}</strong>
        </header>

        <section className="invoice-meta-grid">
          <div><span>Invoice number</span><strong>{invoice.invoice_number}</strong></div>
          <div><span>Issue date</span><strong>{date(invoice.issue_date)}</strong></div>
          <div><span>Due date</span><strong>{date(invoice.due_date)}</strong></div>
          <div><span>Client</span><strong>{invoice.client_name || invoice.business_name}</strong></div>
          <div><span>Business</span><strong>{invoice.business_name || "Not provided"}</strong></div>
          <div><span>Project</span><strong>{invoice.project_name}</strong></div>
        </section>

        <section className="invoice-summary">
          <h2>Invoice Summary</h2>
          {(invoice.line_items || []).map((item, index) => <div className="invoice-line" key={`${item.description}-${index}`}><span>{item.description}</span><strong>{money(item.amount, invoice.currency)}</strong></div>)}
          <div className="invoice-total"><span>Invoice total</span><strong>{money(invoice.subtotal, invoice.currency)}</strong></div>
        </section>

        {isPaid ? <section className="invoice-state paid"><h2>Paid</h2><p>This invoice has been paid.</p><p>{invoice.payment_method && `Payment method: ${invoice.payment_method === "ach" ? "Bank Account (ACH)" : "Card"}`}</p><p>{Number(invoice.ach_discount_amount || 0) > 0 && `ACH discount: ${money(invoice.ach_discount_amount, invoice.currency)}`}</p><p>{Number(invoice.amount_collected || 0) > 0 && `Amount collected: ${money(invoice.amount_collected, invoice.currency)}`}</p></section> : isProcessing ? <section className="invoice-state processing"><h2>Bank payment submitted</h2><p>Your payment is processing. We'll update this invoice automatically when the bank payment is confirmed.</p></section> : <section className="invoice-payment-section">
          <h2>Choose Payment Method</h2>
          <div className="invoice-method-grid">
            <button type="button" className={(selectedMethod === "ach" ? "selected " : "") + "recommended"} disabled={intentLoading} onClick={() => prepareIntent("ach")}>
              <span>Bank Account (ACH) — Recommended</span>
              <small>Save on your invoice when you pay directly from your bank account.</small>
              <strong>{money(achOption?.amount_due, invoice.currency)}</strong>
              {Number(achOption?.ach_discount || 0) > 0 && <em>Save {money(achOption.ach_discount, invoice.currency)}</em>}
            </button>
            <button type="button" className={selectedMethod === "card" ? "selected" : ""} disabled={intentLoading} onClick={() => prepareIntent("card")}>
              <span>Card</span>
              <small>Pay by debit or credit card.</small>
              <strong>{money(cardOption?.amount_due, invoice.currency)}</strong>
            </button>
          </div>
          {selectedOption && <p className="invoice-method-note">{selectedMethod === "ach" ? "Bank Account / ACH" : "Card"} total: <strong>{money(selectedOption.amount_due, invoice.currency)}</strong></p>}
          {!intent && <button type="button" className="invoice-prepare-button" disabled={intentLoading} onClick={() => prepareIntent(selectedMethod)}>{intentLoading ? "Preparing secure payment..." : `Continue with ${selectedMethod === "ach" ? "Bank Account (ACH)" : "Card"}`}</button>}
          {intent?.client_secret && intent?.publishable_key && <Elements key={`${intent.payment_intent_id || intent.client_secret}:${intent.payment_method}`} stripe={stripePromiseFor(intent.publishable_key)} options={{ clientSecret: intent.client_secret }}><EmbeddedPaymentForm invoice={invoice} intent={intent} onProcessing={processing} onError={setError} /></Elements>}
          {message && <p className="invoice-success-message">{message}</p>}
          {error && <p className="invoice-error-message">{error}</p>}
        </section>}
      </section>
    </main>
  );
}